import { Injectable } from '@angular/core';
import { SubmoduleCoreService } from './submodule-core.service';
import { ApiService } from '../../api/api.service';
import { ModuleService } from '../module/module.service';
import { ProjectSubmodule } from './project-submodule.interface';
import { ToastService } from '../../services/toast/toast.service';
import { TranslateService } from '../../utilities/translate/translate.service';
import { ProjectService } from '../project/project.service';
import { ProjectElement } from '../element/project-element.interface';
import { Observable } from 'rxjs';
import { ProjectModule } from '../module/project-module.interface';
import { ModalService } from '../../services/modal/modal.service';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Submodule service.
 *
 * The public interface for the SubmoduleCoreService, with shorthands for
 * each operation.
 */
@Injectable({
  providedIn: 'root'
})
export class SubmoduleService extends SubmoduleCoreService {

  /** Constructor. */
  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    private modalService: ModalService,
    protected moduleService: ModuleService,
    protected projectService: ProjectService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    super(apiService, apiLegacyService, moduleService, projectService);
  }


   /**
   * Add a new submodule to te module.
   *
   * @note The new submodule does not contain elements yet.
   * @note Don't forget to `subscribe()`, otherwise it doesn't work.
   * @note The toast is added to a timeout, so it's added to the js event loop. Without, it's added
   *  to the Observable which immediately completes the toast call (only visible for split second).
   *  Due to Material Snackbar not supporting stacked toasts, it's disabled for now.
   *
   * @param module The parent module to add the submodule to.
   * @returns API response with the new submodule.
   */
  public add(module: ProjectModule): Observable<ProjectSubmodule> {
    const submodule = {
      moduleId: module._id,
      name: this.translateService.get('newSubmodule')
    }  as Partial<ProjectSubmodule>;
    return new Observable(observer => {
      this.apiLegacyService.createSubmodule(submodule).subscribe(response => {
        this.projectService.addSubmodule({ ...submodule, ...response });
        observer.next(response);
        observer.complete();
        // setTimeout(() => {
        //   this.toastService.open(this.translateService.get('submoduleAdded'));
        // });
      });
    });
  }

  /**
   * Delete submodule.
   *
   * @param submodule The submodule to delete.
   * @param [confirm=false] Whether to show confirm modal before deletion.
   */
  public delete(submodule: ProjectSubmodule, confirm: boolean = false): void {
    if (confirm) {
      this.modalService.confirm({
        title: this.translateService.get('deletesubmoduletitle'),
        content: this.translateService.get('areyousuredeletesubmodule'),
        action: this.translateService.getPlural('delete'),
        actionIcon: 'delete',
        type: 'warn'
      }).afterClosed().subscribe((click: any) => {
        if (click === 'confirm') {
          this.delete(submodule);
        }
      });
    } else {
      this._delete(submodule).subscribe(() => {
        this.toastService.open(this.translateService.get('deletedsubmodule'));
      });
    }
  }

  /**
   * Get submodule name.
   *
   * @param id Requested submodule's id.
   * @returns Name of the submodule.
   */
  public getName(id: ProjectSubmodule['_id']): ProjectSubmodule['name'] {
    return this.getSubmodule(id).name;
  }

  /**
   * Reorder elements within the submodule.
   *
   * @note This call only works when `order: number` is deleted from the submodule, otherwise
   *  it tries to sort the submodule, instead of the elements.
   *
   * @param submodule The parent submodule.
   * @param elements The updated element list.
   */
  public reorderElements(elements: ProjectElement[]): void {
    const submodule = this.getSubmodule(elements[0].submoduleId);
    delete submodule.order;
    this._patchSubmodule(submodule, { elements: elements }).subscribe(() => {
      this.toastService.open(this.translateService.get('reordered'));
    });
  }

  /**
   * Set name (title).
   *
   * @param submodule The submodule to set the name (title) for.
   * @param name The new name (title).
   */
  public setName(submodule: ProjectSubmodule, name: ProjectSubmodule['name']): void {
    this._patchSubmodule(submodule, { name: name }).subscribe(() => {
      this.toastService.open(this.translateService.get('submoduleUpdated'));
    });
  }

  /**
   * Reorder submodules.
   *
   * @param submodule The submodule to reorder.
   * @param order The new index.
   */
  public setOrder(submodule: ProjectSubmodule, order: ProjectSubmodule['order']): void {
    const submodules = this.moduleService.getModule(submodule.moduleId).submodules;
    const submoduleIndex = submodules.findIndex(m => m._id === submodule._id);
    moveItemInArray(submodules, submoduleIndex, order);
    this._patchSubmodule(submodule, { order: order }).subscribe(() => {
      let index = 0;
      for (const submod of submodules) {
        if (index !== submod.order) {
          this.projectService.patchSubmodule(submod, { order: index });
        }
        index++;
      }
      this.toastService.open(this.translateService.get('submodulesReordered'));
    });
  }
}
